/**
 * PDF Export Utility
 * Common utility for generating PDFs across the application with branding
 * Uses jsPDF library
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoImage from '../assets/logo_optibuild.png';

export interface PDFBrandingConfig {
  companyName?: string;
  companyLogo?: string; // Base64 or URL
  primaryColor?: string;
  secondaryColor?: string;
}

export interface PDFExportOptions {
  title: string;
  subtitle?: string;
  documentNumber?: string;
  date?: string;
  branding?: PDFBrandingConfig;
  footer?: string;
}

class PDFUtils {
  private defaultBranding: PDFBrandingConfig = {
    companyName: 'OptiBuild',
    primaryColor: '#1e40af', // Blue-700
    secondaryColor: '#64748b' // Slate-500
  };

  // Store current document options for header rendering on new pages
  private currentOptions: PDFExportOptions | null = null;

  /**
   * Get current theme colors from localStorage
   */
  private getThemeColors(): { primary: string; secondary: string } {
    const savedTheme = localStorage.getItem('optibuild-theme');

    const themes: Record<string, { primary: string; secondary: string }> = {
      'default': { primary: '#e97316', secondary: '#0ea5e9' },
      'earth': { primary: '#059669', secondary: '#d97706' },
      'industrial': { primary: '#1e40af', secondary: '#0891b2' },
      'safety': { primary: '#f59e0b', secondary: '#ef4444' },
      'slate': { primary: '#475569', secondary: '#0ea5e9' }
    };

    return themes[savedTheme || 'default'] || themes['default'];
  }

  /**
   * Add header to a page (used for first page and subsequent pages)
   */
  private addHeader(doc: jsPDF, options: PDFExportOptions): void {
    const branding = { ...this.defaultBranding, ...options.branding };
    const themeColors = this.getThemeColors();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Use theme colors if not overridden by branding
    const primaryColor = branding.primaryColor || themeColors.primary;
    const secondaryColor = branding.secondaryColor || themeColors.secondary;

    // Header background
    doc.setFillColor(primaryColor);
    doc.rect(0, 0, pageWidth, 40, 'F');

    // Add logo if available
    const logoToUse = branding.companyLogo || logoImage;
    if (logoToUse) {
      try {
        // Add logo to header (positioned at left side)
        doc.addImage(logoToUse, 'PNG', 15, 8, 25, 25);

        // Adjust company name position to be next to logo
        doc.setFontSize(20);
        doc.setTextColor(255, 255, 255);
        doc.text(branding.companyName || 'OptiBuild', 45, 20);

        // Document title
        doc.setFontSize(16);
        doc.text(options.title, 45, 32);
      } catch (error) {
        // Fallback if logo fails to load
        console.warn('Failed to add logo to PDF:', error);
        doc.setFontSize(20);
        doc.setTextColor(255, 255, 255);
        doc.text(branding.companyName || 'OptiBuild', 15, 20);

        doc.setFontSize(16);
        doc.text(options.title, 15, 32);
      }
    } else {
      // Company name/logo (no logo available)
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text(branding.companyName || 'OptiBuild', 15, 20);

      // Document title
      doc.setFontSize(16);
      doc.text(options.title, 15, 32);
    }

    // Document info bar with theme secondary color
    doc.setFillColor(secondaryColor);
    doc.rect(0, 40, pageWidth, 10, 'F');

    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    const infoY = 47;

    if (options.documentNumber) {
      doc.text(`Doc #: ${options.documentNumber}`, 15, infoY);
    }

    if (options.date) {
      doc.text(`Date: ${options.date}`, pageWidth - 60, infoY);
    }
  }

  /**
   * Initialize a new PDF document with branding
   */
  private initializePDF(options: PDFExportOptions): jsPDF {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Store options for use when adding new pages
    this.currentOptions = options;

    // Add header to first page
    this.addHeader(doc, options);

    // Add subtitle if provided
    if (options.subtitle) {
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(options.subtitle, 15, 60);
    }

    return doc;
  }

  /**
   * Check if content will overflow page and add new page if needed
   * Returns the Y position to use (either current or reset to top of new page)
   */
  private checkPageOverflow(doc: jsPDF, currentY: number, contentHeight: number): number {
    const pageHeight = doc.internal.pageSize.getHeight();
    const bottomMargin = 25; // Reserve space for footer
    const topMargin = 60; // Space after header on new pages

    if (currentY + contentHeight > pageHeight - bottomMargin) {
      doc.addPage();

      // Add header to new page if we have current options
      if (this.currentOptions) {
        this.addHeader(doc, this.currentOptions);
      }

      return topMargin;
    }

    return currentY;
  }

  /**
   * Add footer to all pages
   */
  private addFooter(doc: jsPDF, options: PDFExportOptions): void {
    const pageCount = doc.getNumberOfPages();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Generate timestamp
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const timeStr = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      // Footer line
      doc.setDrawColor(200, 200, 200);
      doc.line(15, pageHeight - 20, pageWidth - 15, pageHeight - 20);

      // Footer text (left side)
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);

      const footerText = options.footer || `Generated by ${options.branding?.companyName || 'OptiBuild'}`;
      doc.text(footerText, 15, pageHeight - 15);

      // Date and timestamp (center)
      const dateTimeText = `${dateStr} at ${timeStr}`;
      const dateTimeWidth = doc.getTextWidth(dateTimeText);
      doc.text(dateTimeText, (pageWidth - dateTimeWidth) / 2, pageHeight - 15);

      // Page number (right side)
      const pageText = `Page ${i} of ${pageCount}`;
      const pageTextWidth = doc.getTextWidth(pageText);
      doc.text(pageText, pageWidth - 15 - pageTextWidth, pageHeight - 15);
    }
  }

  /**
   * Add a section header
   */
  addSectionHeader(doc: jsPDF, title: string, y: number, branding?: PDFBrandingConfig): number {
    const themeColors = this.getThemeColors();
    const primaryColor = branding?.primaryColor || themeColors.primary;

    // Check if we need a new page (header needs ~15mm)
    y = this.checkPageOverflow(doc, y, 15);

    doc.setFontSize(14);
    doc.setTextColor(primaryColor);
    doc.text(title, 15, y);

    // Underline
    doc.setDrawColor(primaryColor);
    doc.setLineWidth(0.5);
    doc.line(15, y + 2, 195, y + 2);

    return y + 10; // Return next Y position
  }

  /**
   * Add key-value pairs in a grid
   */
  addKeyValueGrid(
    doc: jsPDF,
    data: { label: string; value: string | number }[],
    y: number,
    columns: number = 2
  ): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const columnWidth = (pageWidth - 30) / columns;
    const rowHeight = 8;

    doc.setFontSize(9);

    let currentY = y;
    let currentX = 15;
    let itemsInRow = 0;

    data.forEach((item, index) => {
      // Check if we need a new page before adding this row
      if (itemsInRow === 0) {
        currentY = this.checkPageOverflow(doc, currentY, rowHeight + 5);
      }

      // Label
      doc.setTextColor(100, 100, 100);
      doc.text(item.label, currentX, currentY);

      // Value
      doc.setTextColor(0, 0, 0);
      doc.text(String(item.value), currentX, currentY + 5);

      itemsInRow++;

      if (itemsInRow >= columns) {
        currentY += rowHeight + 5;
        currentX = 15;
        itemsInRow = 0;
      } else {
        currentX += columnWidth;
      }
    });

    return itemsInRow > 0 ? currentY + rowHeight + 5 : currentY;
  }

  /**
   * Add a table
   */
  addTable(
    doc: jsPDF,
    headers: string[],
    rows: (string | number)[][],
    y: number,
    branding?: PDFBrandingConfig
  ): number {
    const themeColors = this.getThemeColors();
    const primaryColor = branding?.primaryColor || themeColors.primary;

    autoTable(doc, {
      startY: y,
      head: [headers],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: primaryColor,
        textColor: '#ffffff',
        fontSize: 10,
        fontStyle: 'bold'
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      alternateRowStyles: {
        fillColor: '#f9fafb'
      }
    });

    return (doc as any).lastAutoTable.finalY + 10;
  }

  /**
   * Add text block with word wrap
   */
  addTextBlock(doc: jsPDF, label: string, text: string, y: number, maxWidth: number = 180): number {
    const pageHeight = doc.internal.pageSize.getHeight();
    const bottomMargin = 25;
    const topMargin = 60;
    const lineHeight = 5;

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);

    // Check if label fits on current page
    if (label) {
      y = this.checkPageOverflow(doc, y, 10);
      doc.text(label, 15, y);
      y += 6;
    }

    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    const lines = doc.splitTextToSize(text, maxWidth);

    // Add lines, checking for page overflow
    for (let i = 0; i < lines.length; i++) {
      // Check if current line will fit
      if (y + lineHeight > pageHeight - bottomMargin) {
        doc.addPage();

        // Add header to new page
        if (this.currentOptions) {
          this.addHeader(doc, this.currentOptions);
        }

        y = topMargin;
      }

      doc.text(lines[i], 15, y);
      y += lineHeight;
    }

    return y + 5; // Add spacing after text block
  }

  /**
   * Export to PDF with save dialog
   */
  async exportToPDF(
    doc: jsPDF,
    filename: string,
    options: PDFExportOptions
  ): Promise<void> {
    this.addFooter(doc, options);
    doc.save(filename);
  }

  /**
   * Create and export a simple document
   */
  async createSimplePDF(
    options: PDFExportOptions,
    sections: Array<{
      title: string;
      content: string | { label: string; value: string | number }[];
    }>,
    filename: string
  ): Promise<void> {
    const doc = this.initializePDF(options);
    let y = options.subtitle ? 70 : 60;

    sections.forEach(section => {
      y = this.addSectionHeader(doc, section.title, y, options.branding);

      if (typeof section.content === 'string') {
        y = this.addTextBlock(doc, '', section.content, y);
      } else {
        y = this.addKeyValueGrid(doc, section.content, y);
      }

      y += 5; // Space between sections
    });

    await this.exportToPDF(doc, filename, options);
  }

  /**
   * Create a PDF instance for custom building
   */
  createPDF(options: PDFExportOptions): jsPDF {
    return this.initializePDF(options);
  }

  /**
   * Get default branding
   */
  getDefaultBranding(): PDFBrandingConfig {
    return { ...this.defaultBranding };
  }

  /**
   * Set default branding for all PDFs
   */
  setDefaultBranding(branding: Partial<PDFBrandingConfig>): void {
    this.defaultBranding = { ...this.defaultBranding, ...branding };
  }
}

// Export singleton instance
export const pdfUtils = new PDFUtils();
